import { ColumnDef } from "@tanstack/react-table";
import { Fragment, ReactNode } from "react";
import { Card, Employee, TerminologyOptions } from "@/types/v2";
import { HeaderSortable } from "@/components/Base/HeaderSortable";
import { firestoreToDateTime } from "@/lib/utils";
import { formatEmployeeName } from "@/lib/employees";
import { Header } from "@/components/Base/Header";
import { format } from "date-fns";
import { Badge } from "@/components/Base/Badge";

interface ColumnProps {
  actions: (card: Card) => ReactNode[];
  employees?: Employee[];
  terminolize?: (
    term: string,
    option: TerminologyOptions,
    form?: "singular" | "plural" | "acronym"
  ) => string;
}

const createActionsColumn = ({ actions }: ColumnProps): ColumnDef<Card> => ({
  accessorKey: "actions",
  header: "",
  cell: ({ row }) => {
    const item = row.original;
    return (
      <div className="flex gap-2">
        {actions(item).map((action, index) => (
          <Fragment key={`action-${item.id}-${index}`}>{action}</Fragment>
        ))}
      </div>
    );
  },
});

export const cardsTableColumns = (props?: ColumnProps) => {
  const { employees = [], terminolize = (term: string) => term } = props || {};

  const columns: ColumnDef<Card>[] = [
    {
      accessorKey: "id",
      meta: {
        label: "ID",
      },
      header: () => <Header title="ID" />,
    },
    {
      accessorKey: "cardNumber",
      meta: {
        label: terminolize(
          "RO Number",
          TerminologyOptions.repairOrderNumber,
          "acronym"
        ),
      },
      header: ({ column }) => (
        <HeaderSortable
          column={column}
          title={terminolize(
            "RO Number",
            TerminologyOptions.repairOrderNumber,
            "acronym"
          )}
        />
      ),
      cell: ({ row }) => {
        const cardNumber = row.getValue("cardNumber") as string;
        return <div className="font-medium">{cardNumber || "—"}</div>;
      },
    },
    {
      accessorKey: "customer",
      meta: {
        label: "Customer",
      },
      header: () => <Header title="Customer" />,
      cell: ({ row }) => {
        const customer = row.getValue("customer") as Card["customer"];
        const customerName =
          customer?.firstName && customer?.lastName
            ? `${customer.firstName} ${customer.lastName}`
            : customer?.name || "—";
        return <div className="font-medium">{customerName}</div>;
      },
    },
    {
      accessorKey: "vehicle",
      meta: {
        label: terminolize("Vehicle", TerminologyOptions.vehicle, "singular"),
      },
      header: () => (
        <Header
          title={terminolize("Vehicle", TerminologyOptions.vehicle, "singular")}
        />
      ),
      cell: ({ row }) => {
        const vehicle = row.getValue("vehicle") as Card["vehicle"];
        const vehicleInfo =
          vehicle?.year && vehicle?.make && vehicle?.model
            ? `${vehicle.year} ${vehicle.make} ${vehicle.model}`
            : "—";
        return <div className="text-sm">{vehicleInfo}</div>;
      },
    },
    {
      accessorKey: "status",
      meta: {
        label: "Status",
      },
      header: ({ column }) => <HeaderSortable column={column} title="Status" />,
      cell: ({ row }) => {
        const status = row.getValue("status") as string;
        return <Badge variant="secondary">{status || "Unknown"}</Badge>;
      },
    },
    {
      accessorKey: "currentContainer",
      meta: {
        label: "Container",
      },
      header: () => <Header title="Container" />,
      cell: ({ row }) => {
        const container = row.getValue("currentContainer") as string;
        return <div className="text-sm">{container || "—"}</div>;
      },
    },
    {
      accessorKey: "technicianId",
      meta: {
        label: "Technician",
      },
      header: () => <Header title="Technician" />,
      cell: ({ row }) => {
        const technicianId = row.getValue("technicianId") as string;
        const technician = employees?.find((emp) => emp.id === technicianId);
        return <div className="text-sm">{formatEmployeeName(technician)}</div>;
      },
    },
    {
      accessorKey: "createdAt",
      meta: {
        label: "Created",
      },
      header: ({ column }) => (
        <HeaderSortable column={column} title="Created" />
      ),
      cell: ({ row }) => {
        const createdAt = row.original.createdAt;
        const date = firestoreToDateTime(createdAt);
        return (
          <div className="text-sm">
            {date ? format(date, "MMM d, yyyy") : "—"}
          </div>
        );
      },
    },
    {
      accessorKey: "updatedAt",
      meta: {
        label: "Updated",
      },
      header: ({ column }) => (
        <HeaderSortable column={column} title="Updated" />
      ),
      cell: ({ row }) => {
        const updatedAt = row.original.updatedAt;
        const date = firestoreToDateTime(updatedAt);
        return (
          <div className="text-sm">
            {date ? format(date, "MMM d, yyyy") : "—"}
          </div>
        );
      },
    },
  ];

  // Add actions column if provided
  if (props?.actions) {
    columns.push(createActionsColumn(props));
  }

  return columns;
};
